/*
 * Copyright 2026 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 */
package ai.rules

/**
 * The platform-neutral half of the "re-bucket a category" surface — web's
 * BucketEditModal, Android's BucketEditSheet, iOS's BucketEditSheet.
 *
 * Those three surfaces own their own markup and their own HTTP client and
 * nothing else: the row model, the shadowing rules and — above all — the
 * **request ordering contract** in [applySet]/[applyClear] live here, once.
 * `budget_category_classifications` is a single global table shared by all
 * three clients, so a platform that ordered its writes differently would be
 * writing a different meaning into the same rows.
 *
 * Rows are ordered by dollar impact, not alphabetically: the two or three
 * categories that actually move the 50/30/20 ratio belong on top. Nobody audits
 * a taxonomy for fun — they come here because a number looked wrong.
 */
object BucketOverrideEditor {

    /** A detailed strand under a primary. [code] is what an override is written against. */
    data class Sub(val code: String, val amount: Double)

    /** One primary category with every detailed strand under it, regardless of bucket. */
    data class Row(val code: String, val amount: Double, val subs: List<Sub>)

    /**
     * The two writes an editor makes, behind an interface so the precedence
     * rules in [applySet]/[applyClear] can be tested without a network — the
     * request *sequence* is part of the contract (see [applySet]), and only a
     * recording fake can assert it. It also keeps this file transport-agnostic:
     * each platform injects its own HTTP client.
     */
    interface Api {
        suspend fun put(categoryCode: String, bucketWire: String): Boolean
        suspend fun delete(categoryCode: String): Boolean
    }

    /**
     * Outcome of one write sequence.
     *
     * [overrides] is the caller's map advanced to whatever actually landed on
     * the server — on failure that is a *partial* application, which is the
     * point: the UI must redraw the state the server is really in, not the one
     * the user asked for. Returned rather than mutated in place so the same
     * function serves Kotlin callers, Compose snapshot state and Swift value
     * dictionaries alike.
     */
    data class EditResult(val ok: Boolean, val overrides: Map<String, BudgetBucket>)

    /**
     * One row per primary category, biggest first.
     *
     * BucketFlowBuilder splits a mixed primary across two BucketNodes (groceries →
     * NEEDS and restaurants → WANTS both sit under FOOD_AND_DRINK), so flattening
     * the buckets emits the same primary twice, each with a partial amount.
     * Grouping by code is what makes "appears under more than one bucket" read as
     * *mixed* rather than as a duplicate row.
     */
    fun buildRows(flow: BucketFlow): List<Row> =
        flow.buckets
            .flatMap { it.primaries }
            .groupBy { it.label }
            .map { (code, nodes) ->
                Row(
                    code = code,
                    amount = nodes.sumOf { it.amount },
                    subs = nodes.flatMap { it.children }
                        .map { Sub(it.label, it.amount) }
                        .sortedByDescending { it.amount },
                )
            }
            .sortedByDescending { it.amount }

    /**
     * Every sub-level key a primary override on [row] would shadow.
     *
     * [Row.subs] only carries the strands that had spend in the month the widget
     * is showing, but `budget_category_classifications` is global: a sub override
     * set in a month with no spend here is invisible to the row model yet still
     * live in the DB, and would resurrect the moment the primary above it is
     * cleared. So the affected set is the month's strands UNIONED with every
     * override the editor holds whose key sits under this primary.
     *
     * The prefix rule is Plaid's: every detailed code in `models.PlaidCategories`
     * is its primary plus `_` plus the leaf (`FOOD_AND_DRINK_GROCERIES`).
     * [Row.code] itself is excluded — it is the primary key, not a sub key.
     */
    fun subKeysOf(row: Row, local: Map<String, BudgetBucket>): List<String> =
        (row.subs.map { it.code } + local.keys.filter { it.startsWith("${row.code}_") })
            .distinct()
            .filter { it != row.code }

    /** Distinct buckets the primary's strands resolve to. More than one = mixed. */
    private fun bucketsOf(row: Row, local: Map<String, BudgetBucket>): List<BudgetBucket> =
        if (row.subs.isEmpty()) listOf(BudgetClassificationEngine.effectiveBucket(row.code, null, local))
        else row.subs.map { BudgetClassificationEngine.effectiveBucket(row.code, it.code, local) }.distinct()

    /** The one bucket every strand of [row] resolves to, or null when it is mixed. */
    fun singleBucket(row: Row, local: Map<String, BudgetBucket>): BudgetBucket? =
        bucketsOf(row, local).singleOrNull()

    /**
     * Whether the row carries any user override at all — its own, or one on a sub
     * beneath it ([subKeysOf], so a choice made in a month this row can't see
     * still lights the reset control).
     */
    fun isOverridden(row: Row, local: Map<String, BudgetBucket>): Boolean =
        local.containsKey(row.code) || subKeysOf(row, local).any { local.containsKey(it) }

    /**
     * Write one override, primary- or sub-level.
     *
     * `effectiveBucket` consults the PRIMARY code before the detailed one, so a
     * primary-level override shadows every sub under it. Two rules follow, and
     * together they are the whole precedence contract of this editor:
     *
     *  - **Setting the primary drops every sub-level override underneath it.** The
     *    user just stated one intent for the group; leaving invisible sub choices
     *    behind would let a later reset resurrect forgotten decisions. "Underneath"
     *    means [subKeysOf], not just this month's strands.
     *  - **Setting a sub first materializes any primary override onto the sibling
     *    subs, then deletes the primary override.** Deleting it without that step
     *    would silently revert every untouched sibling to its engine default — in
     *    every month, not just this one, since the table is global and
     *    BudgetHealthAggregator reads it too.
     *
     * **Order matters and is asserted by tests.** Every request sequence is
     * arranged so the primary override — the shadowing key — is present for the
     * whole window in which the sub keys disagree with the user's intent. Setting
     * a primary PUTs it *before* deleting the subs it shadows; setting a sub PUTs
     * the new sub value and materializes the siblings *before* deleting the
     * primary. A connection dropped mid-sequence therefore leaves the server
     * rendering either the old state or the new one, never a state where the
     * user's earlier choices have been destroyed and nothing replaced them.
     * (PUT is an upsert and DELETE of a missing row returns 200, so a retry is
     * always safe.)
     *
     * A sub whose code equals the primary's has no key of its own; it is rendered
     * read-only by every surface and never reaches this function.
     *
     * Stops at the first failed request, returning an [EditResult] consistent with
     * whatever actually landed on the server.
     */
    suspend fun applySet(
        row: Row,
        code: String,
        bucket: BudgetBucket,
        local: Map<String, BudgetBucket>,
        api: Api,
    ): EditResult {
        val working = local.toMutableMap()
        val subKeys = subKeysOf(row, working)
        if (!api.put(code, bucket.wire)) return EditResult(false, working)
        working[code] = bucket

        if (code == row.code) {
            for (c in subKeys) {
                if (!working.containsKey(c)) continue
                if (!api.delete(c)) return EditResult(false, working)
                working.remove(c)
            }
        } else {
            val primary = working[row.code] ?: return EditResult(true, working)
            for (c in subKeys) {
                if (c == code) continue
                if (!api.put(c, primary.wire)) return EditResult(false, working)
                working[c] = primary
            }
            if (!api.delete(row.code)) return EditResult(false, working)
            working.remove(row.code)
        }
        return EditResult(true, working)
    }

    /**
     * Hand a category back to the engine default. Clearing a primary also clears
     * every sub under it ([subKeysOf], so overrides from months this row can't
     * see go too) — otherwise the row would still be overridden after the user
     * asked for the default, and the forgotten sub choices would resurface.
     *
     * The primary is deleted LAST for the reason given in [applySet]: while it
     * still exists it shadows the subs, so their removal is invisible and a
     * dropped connection can't make a forgotten sub choice pop into view.
     */
    suspend fun applyClear(
        row: Row,
        code: String,
        local: Map<String, BudgetBucket>,
        api: Api,
    ): EditResult {
        val working = local.toMutableMap()
        val codes =
            if (code == row.code) subKeysOf(row, working) + row.code
            else listOf(code)
        for (c in codes) {
            if (!working.containsKey(c)) continue
            if (!api.delete(c)) return EditResult(false, working)
            working.remove(c)
        }
        return EditResult(true, working)
    }
}
